// -----------------------------------------------------------------------------
// Product shot: each shot plays its animation on a loop while it is on screen.
// Without JS or with reduced motion, the finished state from the markup stays.
// -----------------------------------------------------------------------------

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

const RISE = {
  opacity: [0, 1],
  transform: ['translateY(0.6rem)', 'none']
};

const POP = {
  opacity: [0, 1],
  transform: ['scale(0.85)', 'none']
};

const FADE_OUT = {
  opacity: [1, 0]
};

// Returns a wait(ms) that also holds while the shot is off screen, so the loop pauses there
const createWait = (shot) => {
  let onScreen = false;

  new IntersectionObserver(([entry]) => {
    onScreen = entry.isIntersecting;
  }, { threshold: 0.25 }).observe(shot);

  return async (ms) => {
    await new Promise((resolve) => setTimeout(resolve, ms));

    while (!onScreen) {
      await new Promise((resolve) => setTimeout(resolve, 250));
    }
  };
};

const avatarOf = (msg) => msg.querySelector('.pb-av').outerHTML;

// A chat bubble with three bouncing dots, on the AI's or the student's side
const typingBubble = (avatar, isStudent) => {
  const el = document.createElement('div');
  const col = `<div class="pb-msg__col"><span class="pb-msg__who">${isStudent ? 'Student' : 'Packback AI'}</span><p><i></i><i></i><i></i></p></div>`;

  el.className = `pb-msg ${isStudent ? 'pb-msg--me' : 'pb-msg--ai'} pb-msg--typing`;
  el.innerHTML = isStudent ? col + avatar : avatar + col;

  return el;
};

const pressButton = async (el, wait, ms) => {
  el.classList.add('is-pressed');
  await wait(ms);
  el.classList.remove('is-pressed');
};

// Hero: the student's replies type into the box and send, the AI "types" then answers, and the concept map card beside
// the chat checks each reply, then updates the status and evidence.
const playHero = async (shot, wait) => {
  const chat = shot.querySelector('.pb-chat');
  const msgs = [...chat.children];
  const typed = shot.querySelector('.pb-live__typed');
  const turns = shot.querySelector('.pb-live__turns b');
  const send = shot.querySelector('.pb-live__send');
  const aiAvatar = avatarOf(msgs.find((msg) => msg.classList.contains('pb-msg--ai')));
  const card = shot.querySelector('[data-analysis]');
  const pill = card.querySelector('.pb-concept.is-focus .pb-status');
  const count = card.querySelector('.pb-analysis__count');
  const quote = card.querySelector('.pb-analysis__quote');
  const evidence = quote.parentElement;
  const STATUS = {
    none: 'Not Taught',
    part: 'Partially Explained',
    clear: 'Clearly Explained'
  };

  const setStatus = (status) => {
    pill.className = `pb-status pb-status--${status}`;
    pill.textContent = STATUS[status];
  };

  const setTurns = (n) => {
    turns.textContent = String(n);
    turns.nextElementSibling.textContent = n === 1 ? 'turn' : 'turns';
  };

  const show = (el) => {
    chat.append(el);
    el.animate(RISE, {
      duration: 450,
      easing: EASE
    });
  };

  const analyze = async (msg) => {
    card.classList.add('is-busy');
    await wait(2000);
    card.classList.remove('is-busy');
    setStatus(msg.dataset.status);
    pill.animate({
      transform: ['scale(0.8)', 'scale(1.06)', 'none'],
      opacity: [0, 1, 1]
    }, {
      duration: 500,
      easing: EASE
    });
    count.textContent = String(Number(count.textContent) + 1);
    quote.textContent = msg.dataset.evidence;
    evidence.hidden = false;
    quote.animate(RISE, {
      duration: 450,
      easing: EASE
    });
  };

  for (;;) {
    setStatus('none');
    count.textContent = '0';
    evidence.hidden = true;
    card.classList.remove('is-busy');
    chat.replaceChildren();
    typed.textContent = '';
    setTurns(0);

    let n = 0;

    await wait(800);

    for (const msg of msgs) {
      if (msg.classList.contains('pb-msg--me')) {
        // Type the reply into the box in about two and a half seconds, then send it
        const text = msg.querySelector('p').textContent;
        const step = Math.max(1, Math.ceil(text.length / 90));

        for (let i = step; i < text.length; i += step) {
          typed.textContent = text.slice(0, i);
          await wait(30);
        }

        typed.textContent = text;
        await wait(700);
        await pressButton(send, wait, 140);
        typed.textContent = '';
        show(msg);
        analyze(msg);
      } else {
        const dots = typingBubble(aiAvatar, false);

        show(dots);
        await wait(n === 0 ? 1000 : 2200);
        dots.remove();
        show(msg);
      }

      n += 1;
      setTurns(n);
      await wait(2200);
    }

    await wait(7000);
    await chat.animate(FADE_OUT, {
      duration: 400,
      fill: 'forwards'
    }).finished;
    chat.getAnimations().forEach((animation) => animation.cancel());
  }
};

// Rubric: each part of the student's work lights up and the instructor's grade callout pops in; the instructor picks
// the level and the meter fills to it. All three stay up, hold, then it replays.
const playRubric = async (shot, wait) => {
  const ui = shot.querySelector('[data-grade]');
  const parts = [...ui.querySelectorAll('.pb-work__part')];

  for (;;) {
    ui.classList.add('is-grading');
    parts.forEach((part) => {
      part.classList.remove('is-active', 'is-done');
      part.querySelector('.pb-grade').classList.add('is-hidden');
      part.querySelectorAll('.pb-grade__meter i').forEach((step) => step.classList.remove('is-on', 'is-top'));
    });
    await wait(1200);

    for (const part of parts) {
      const grade = part.querySelector('.pb-grade');
      const meter = [...grade.querySelectorAll('.pb-grade__meter i')];
      const level = Number(grade.dataset.level);

      part.classList.add('is-active');
      await wait(900);

      // The instructor picks the level: a press on that step, then the meter and the level label fill in together
      grade.classList.add('is-picking');
      grade.classList.remove('is-hidden');
      await wait(800);
      meter[level - 1].classList.add('is-pick');
      meter.slice(0, level).forEach((step, i) => {
        step.classList.add('is-on');

        if (i === level - 1) {
          step.classList.add('is-top');
        }
      });
      grade.classList.remove('is-picking');
      await wait(500);
      meter[level - 1].classList.remove('is-pick');
      await wait(1800);
      part.classList.remove('is-active');
      part.classList.add('is-done');
      await wait(300);
    }

    ui.classList.remove('is-grading');
    await wait(6500);
    parts.forEach((part) => part.querySelector('.pb-grade').classList.add('is-hidden'));
    await wait(500);
  }
};

// "Critically evaluate AI output": starts on the AI's reply, its assumption gets highlighted, then the student pushes
// back and the correction is tagged.
const playEvaluate = async (shot, wait) => {
  const chat = shot.querySelector('.pb-chat');
  const [ai, reply] = [...chat.children];
  const mark = ai.querySelector('.pb-eval__mark');
  const tag = reply.querySelector('.pb-eval__tag');

  for (;;) {
    chat.replaceChildren(ai);
    mark.classList.remove('is-on');
    tag.style.visibility = 'hidden';
    await wait(1600);
    mark.classList.add('is-on');
    await wait(2200);

    const dots = typingBubble(avatarOf(reply), true);

    chat.append(dots);
    dots.animate(RISE, {
      duration: 450,
      easing: EASE
    });
    await wait(1800);
    dots.remove();
    chat.append(reply);
    reply.animate(RISE, {
      duration: 450,
      easing: EASE
    });
    await wait(900);
    tag.style.visibility = '';
    tag.animate(POP, {
      duration: 450,
      easing: EASE
    });
    await wait(7000);
    await reply.animate(FADE_OUT, {
      duration: 400,
      fill: 'forwards'
    }).finished;
    reply.getAnimations().forEach((animation) => animation.cancel());
  }
};

// "Make their own decisions": each transcript statement lights up and its concept-status callout pops in beside it;
// evidence sentences highlight one by one as the callout counts them.
const playTranscript = async (shot, wait) => {
  const rows = [...shot.querySelectorAll('.pb-tx__row')];

  for (;;) {
    rows.forEach((row) => {
      const count = row.querySelector('.pb-tx__ev b');

      row.classList.remove('is-active');
      row.querySelector('.pb-tx__call').classList.add('is-hidden');
      row.querySelectorAll('.pb-ev').forEach((sentence) => sentence.classList.remove('is-on'));

      if (count) {
        count.textContent = '0';
      }
    });
    await wait(1200);

    for (const row of rows) {
      const count = row.querySelector('.pb-tx__ev b');

      row.classList.add('is-active');
      await wait(900);
      row.querySelector('.pb-tx__call').classList.remove('is-hidden');

      for (const [i, sentence] of [...row.querySelectorAll('.pb-ev')].entries()) {
        await wait(700);
        sentence.classList.add('is-on');

        if (count) {
          count.textContent = String(i + 1);
        }
      }

      await wait(1900);
      row.classList.remove('is-active');
      await wait(300);
    }

    await wait(6500);
  }
};

// "Start with their own ideas": the student types their outline and examples, the "instructor can view" note pops up
// over the plan, then Start Teaching is pressed.
const playPlan = async (shot, wait) => {
  const fields = [...shot.querySelectorAll('.pb-plan__box')];
  const lines = fields.map((field) => [...field.querySelectorAll(':scope > p')].map((p) => [p, p.textContent]));
  const note = shot.querySelector('.pb-plan__call');
  const button = shot.querySelector('.pb-plan__btn');

  for (;;) {
    lines.flat().forEach(([p]) => {
      p.textContent = '';
    });
    note.classList.add('is-hidden');
    await wait(1000);

    for (const [i, field] of fields.entries()) {
      field.classList.add('is-typing');

      for (const [p, text] of lines[i]) {
        for (let k = 1; k <= text.length; k++) {
          p.textContent = text.slice(0, k);
          await wait(45);
        }

        await wait(350);
      }

      field.classList.remove('is-typing');
      await wait(500);
    }

    note.classList.remove('is-hidden');
    await wait(2200);
    await pressButton(button, wait, 180);
    await wait(6500);
  }
};

// "Reflect on how their thinking changed": the student types a reflection about a moment in their conversation,
// Submit becomes active and is pressed.
const playReflect = async (shot, wait) => {
  const field = shot.querySelector('.pb-refl__box');
  const typed = shot.querySelector('.pb-refl__typed');
  const text = typed.textContent;
  const submit = shot.querySelector('.pb-refl__btn--go');

  for (;;) {
    typed.textContent = '';
    submit.classList.remove('is-ready');
    await wait(1400);
    field.classList.add('is-typing');

    for (let k = 2; k <= text.length; k += 2) {
      typed.textContent = text.slice(0, k);
      await wait(55);
    }

    typed.textContent = text;
    await wait(500);
    field.classList.remove('is-typing');
    submit.classList.add('is-ready');
    await wait(1100);
    await pressButton(submit, wait, 180);
    await wait(6500);
  }
};

const PLAYERS = {
  hero: playHero,
  rubric: playRubric,
  flow: playEvaluate,
  statuses: playTranscript,
  plan: playPlan,
  reflect: playReflect
};

export default (shot) => {
  const play = PLAYERS[shot.dataset.shot];

  if (!play || !('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    return;
  }

  play(shot, createWait(shot));
};
